import React, { useState } from "react";
import "./WhatsAppPreview.scss";
import { useStore } from "../../../../stores";
import useMobileResponsive from "../../../../hooks/useMobileResponsive";
import clsx from "clsx";
import {
  FormElement,
  FormElementInput,
  NavigateAction,
  WhatsappFlow,
} from "../../../../models/WhatsappFlow";
import { Form, message } from "antd";

interface WhatsAppPreviewProps {
  formData: WhatsappFlow;
  defaultFormValues?: Record<string, any>;
  activeScreenId: string;
  onScreenChange: (screenId: string) => void;
  isFullMode?: boolean;
  isDynamic?: boolean;
  onCompleted?: (values: Record<string, any>) => void;
}

const WhatsAppPreview: React.FC<WhatsAppPreviewProps> = ({
  formData,
  defaultFormValues,
  activeScreenId,
  onScreenChange,
  isFullMode,
  isDynamic,
  onCompleted,
}) => {
  const { selectedBot } = useStore("bots");
  const { selectedChat, fetchWaFlowResponse } = useStore("chats");
  const [form] = Form.useForm();
  const [fieldValues, setFieldValues] = useState<Record<string, any>>({});
  const [flowResponseData, setFlowResponseData] = useState<Record<
    string,
    any
  > | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [isFlowOpen, setIsFlowOpen] = useState(false);
  const isMobile = useMobileResponsive();

  const currentScreen = formData?.flow?.screens?.find(
    (screen) => screen.id === activeScreenId
  );

  const fetchLatestFlowResponse = async () => {
    // Use formData.metaId instead of formData.flow.metaId
    if (!isDynamic || !selectedBot || !formData?.metaId || !selectedChat?.id)
      return;

    setIsLoading(true);
    try {
      const response = await fetchWaFlowResponse(
        selectedBot.id,
        selectedChat?.id,
        formData.metaId,
        "latest"
      );

      if (response) {
        const responseData = response.response;
        if (responseData) {
          setFlowResponseData(responseData);
        }
      }
    } catch (error) {
      console.error("Error fetching flow response:", error);
      message.error("Failed to load previous responses");
    } finally {
      setIsLoading(false);
    }
  };

  // Remove the useEffect as we're now fetching directly in handlePreviewFlow

  const handlePreviewFlow = async () => {
    if (isDynamic) {
      await fetchLatestFlowResponse();
    }
    setIsFlowOpen(true);
  };

  const handleCloseFlow = () => {
    setIsFlowOpen(false);
  };

  if (!selectedBot) return null;

  // Helper function to render form elements based on their type
  const renderFormElement = (element: FormElement): React.ReactNode => {
    switch (element.type) {
      case "TextHeading":
        return (
          <div className="flow-field">
            <h1 className="text-heading">{element.text}</h1>
          </div>
        );
      case "TextSubheading":
        return (
          <div className="flow-field">
            <h2 className="text-subheading">{element.text}</h2>
          </div>
        );
      case "TextCaption":
        return (
          <div className="flow-field">
            <p className="text-caption">{element.text}</p>
          </div>
        );
      case "TextBody":
        return (
          <div className="flow-field">
            <p className="text-body">{element.text}</p>
          </div>
        );
      case "Image":
        return (
          <div className="flow-field">
            <div className="field-image">
              <img
                src={element.src || "/assets/logo.svg"}
                alt="Flow"
                style={{
                  height: element.height ? `${element.height}px` : "auto",
                  width: element.width ? `${element.width}px` : "auto",
                  objectFit: element["scale-type"],
                }}
              />
            </div>
          </div>
        );
      default:
        return (
          <div className="flow-field">
            <div className="field-question">
              Unsupported field type: {(element as any).type}
            </div>
          </div>
        );
    }
  };

  return (
    <div
      style={{ width: isMobile || isFullMode ? "100%" : "35%" }}
      className={clsx("whatsapp-app white whatsapp-preview", {
        "pl-5": !isMobile && !isFullMode,
      })}
    >
      <div className="iphone is-relative">
        <div className="bar bar--ios bar--user">
          <h4 className="title mb-0 is-6">{selectedBot.name}</h4>
        </div>
        <div className="chat-area">
          <div className="bar bar--conversation js-conversation">
            <div
              className="message message--else"
              style={{
                whiteSpace: "break-spaces",
              }}
            >
              <p className="py-4"></p>
              <button
                className={clsx("button is-primary is-small preview-button", {
                  "is-loading": isLoading,
                })}
                onClick={handlePreviewFlow}
                type="button"
                disabled={isLoading}
              >
                <span className="icon is-small">
                  <i className="fas fa-eye"></i>
                </span>
                <span>Preview Flow</span>
              </button>
            </div>
          </div>
        </div>

        {isFlowOpen && (
          <>
            <div className="flow-overlay" onClick={handleCloseFlow}></div>
            <div className="flow-popover" onClick={(e) => e.stopPropagation()}>
              {currentScreen &&
                (() => {
                  const footer =
                    currentScreen.layout?.children?.[0]?.children?.find(
                      (f) => f.type === "Footer"
                    );
                  return (
                    <div key={currentScreen.id} className="flow-page">
                      {/* Flow Header */}
                      <div className="flow-header">
                        <button
                          className="button is-white is-small"
                          onClick={handleCloseFlow}
                        >
                          <span className="icon">
                            <i className="fas fa-times"></i>
                          </span>
                        </button>
                        <span className="flow-title">
                          {currentScreen.title}
                        </span>
                        <button className="button is-white is-small">
                          <span className="icon">
                            <i className="fas fa-ellipsis-v"></i>
                          </span>
                        </button>
                      </div>

                      <Form
                        form={form}
                        initialValues={flowResponseData || defaultFormValues}
                        onFinish={async (values) => {
                          const allValues = {
                            ...fieldValues,
                            ...values,
                          };
                          setFieldValues(allValues);

                          if (
                            (footer as any)["on-click-action"].name ===
                            "complete"
                          ) {
                            if (isDynamic && onCompleted) {
                              onCompleted(allValues);
                            } else {
                              setIsFlowOpen(false);
                            }
                          } else {
                            onScreenChange(
                              (
                                (footer as any)[
                                  "on-click-action"
                                ] as NavigateAction
                              ).next.name
                            );
                          }
                        }}
                      >
                        {/* Flow Content */}
                        <div className="flow-content">
                          {currentScreen.layout?.children?.map(
                            (form, formIndex) => (
                              <div key={formIndex} className="flow-form">
                                {form.children
                                  ?.filter((f) => f.type !== "Footer")
                                  ?.map(
                                    (element: FormElement, elementIndex) => (
                                      <React.Fragment key={elementIndex}>
                                        {[
                                          "TextInput",
                                          "TextArea",
                                          "DatePicker",
                                          "RadioButtonsGroup",
                                          "CheckboxGroup",
                                          "Dropdown",
                                          "OptIn",
                                        ].includes(element.type) ? (
                                          <Form.Item
                                            name={
                                              (element as FormElementInput).name
                                            }
                                            noStyle
                                            rules={
                                              (element as FormElementInput)
                                                .required && isDynamic
                                                ? [
                                                    {
                                                      required: true,
                                                      message: `Please ${
                                                        element.type === "OptIn"
                                                          ? "checked"
                                                          : "enter"
                                                      } ${
                                                        (
                                                          element as FormElementInput
                                                        ).label
                                                      }`,
                                                    },
                                                  ]
                                                : []
                                            }
                                          >
                                            <DynamicFormElement
                                              isDynamic={isDynamic}
                                              element={
                                                element as FormElementInput
                                              }
                                            />
                                          </Form.Item>
                                        ) : (
                                          <>{renderFormElement(element)}</>
                                        )}
                                        {[
                                          "RadioButtonsGroup",
                                          "CheckboxGroup",
                                        ].includes(element.type) &&
                                          elementIndex <
                                            form.children.length - 2 && (
                                            <div className="field-bottom-border" />
                                          )}
                                      </React.Fragment>
                                    )
                                  )}
                              </div>
                            )
                          )}
                        </div>
                        <div className="flow-footer">
                          {footer && (
                            <button
                              className="button is-fullwidth continue-button"
                              type="submit"
                            >
                              {(footer as any).label}
                            </button>
                          )}
                          <span className="managed-by">Managed by EDNA. </span>
                          {/* <a href="#" className="learn-more">
                          Learn more
                        </a> */}
                        </div>
                      </Form>
                    </div>
                  );
                })()}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const DynamicFormElement = ({
  element,
  value,
  onChange,
  isDynamic,
}: {
  value?: any;
  onChange?: (value?: any) => void;
  element: FormElementInput;
  isDynamic?: boolean;
}) => {
  const form = Form.useFormInstance();

  const fieldError = form.getFieldError(element.name);
  const hasError = fieldError.length > 0;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownInfo, setDropdownInfo] = useState<
    | {
        title: string;
        options: any[];
      }
    | undefined
  >();

  if (element.type === "TextInput") {
    return (
      <div className="flow-field">
        <div className="field-input">
          <input
            className={clsx("input", {
              error: hasError,
            })}
            value={value || ""}
            onChange={(e) => onChange?.(e.target.value)}
            type={element["input-type"]}
            placeholder={`${element.label}${
              element.required ? "" : " (optional)"
            }`}
            readOnly={!isDynamic}
          />
        </div>
        {hasError && <div className="field-error">{fieldError[0]}</div>}
      </div>
    );
  }
  if (element.type === "TextArea") {
    return (
      <div className="flow-field">
        <div className="field-textarea">
          <textarea
            value={value || ""}
            onChange={(e) => onChange?.(e.target.value)}
            className={clsx("textarea", {
              error: hasError,
            })}
            placeholder={`${element.label}${
              element.required ? "" : " (optional)"
            }`}
            readOnly={!isDynamic}
          ></textarea>
          {!hasError && <div className="character-count">0 / 600</div>}
        </div>
        {hasError && (
          <div className="is-flex is-justify-content-space-between">
            <div className="field-error mt-2">{fieldError[0]}</div>
            <div className="character-count">0 / 600</div>
          </div>
        )}
      </div>
    );
  }
  if (element.type === "DatePicker") {
    return (
      <div className="flow-field">
        <div className="field-input">
          <input
            value={value || ""}
            onChange={(e) => onChange?.(e.target.value)}
            className={clsx("date", {
              error: hasError,
            })}
            type="date"
            placeholder={`${element.label}${
              element.required ? "" : " (optional)"
            }`}
            readOnly={!isDynamic}
          />
        </div>
        {hasError && <div className="field-error">{fieldError[0]}</div>}
      </div>
    );
  }
  if (element.type === "RadioButtonsGroup") {
    return (
      <div className="flow-field">
        <div className="field-choices">
          <div className="choice-label">
            {element.label}
            {element.required ? "" : " (optional)"}
          </div>
          {element["data-source"]?.map((option, optionIndex: number) => (
            <div key={option.id} className="choice-option">
              <span className="option-text">{option.title}</span>
              <input
                type="radio"
                className="radio-button"
                checked={value === option.id}
                onChange={() => {
                  if (onChange) {
                    onChange(option.id);
                  }
                }}
                readOnly={!isDynamic}
              />
            </div>
          )) || (
            <>
              <div className="choice-option">
                <span className="option-text">Option 1</span>
                <input
                  type="radio"
                  className="radio-button"
                  checked={value === "option1"}
                  readOnly={!isDynamic}
                  onChange={() => {
                    if (onChange) {
                      onChange("option1");
                    }
                  }}
                />
              </div>
            </>
          )}
        </div>
        {hasError && <div className="field-error">{fieldError[0]}</div>}
      </div>
    );
  }
  if (element.type === "CheckboxGroup") {
    return (
      <div className="flow-field">
        <div className="field-choices checkbox-group">
          <div className="choice-label">
            {element.label}
            {element.required ? "" : " (optional)"}
          </div>
          {element["data-source"]?.map((option) => (
            <div key={option.id} className="choice-option">
              <span className="option-text">{option.title}</span>
              <input
                type="checkbox"
                checked={(value || []).includes(option.id)}
                onChange={(e) => {
                  if (onChange) {
                    const newValue = e.target.checked
                      ? [...(value || []), option.id]
                      : (value || []).filter((v: string) => v !== option.id);
                    onChange(newValue);
                  }
                }}
              />
            </div>
          )) || (
            <>
              <div className="choice-option">
                <span className="option-text">Option 1</span>
                <input
                  type="checkbox"
                  checked={(value || []).includes("option1")}
                  onChange={(e) => {
                    if (onChange) {
                      const newValue = e.target.checked
                        ? [...(value || []), "option1"]
                        : (value || []).filter((v: string) => v !== "option1");
                      onChange(newValue);
                    }
                  }}
                  readOnly={!isDynamic}
                />
              </div>
            </>
          )}
        </div>
        {hasError && <div className="field-error">{fieldError[0]}</div>}
      </div>
    );
  }
  if (element.type === "Dropdown") {
    return (
      <>
        {isDropdownOpen && dropdownInfo && (
          <>
            <div
              className="flow-overlay"
              onClick={() => {
                setIsDropdownOpen(false);
                setDropdownInfo(undefined);
              }}
            ></div>
            <div className="flow-popover" onClick={(e) => e.stopPropagation()}>
              <div className="flow-page">
                {/* Flow Header */}
                <div className="flow-header">
                  <button
                    className="button is-white is-small"
                    onClick={() => {
                      setIsDropdownOpen(false);
                      setDropdownInfo(undefined);
                    }}
                  >
                    <span className="icon">
                      <i className="fas fa-arrow-left"></i>
                    </span>
                  </button>
                  <span className="flow-title">{dropdownInfo.title}</span>
                  <button className="button is-white is-small">
                    <span className="icon">
                      <i className="fas fa-ellipsis-v"></i>
                    </span>
                  </button>
                </div>

                {/* Flow Content */}
                <div className="flow-content">
                  <div className="flow-field">
                    <div className="field-choices">
                      {dropdownInfo.options?.map((option) => (
                        <div
                          className="choice-option is-clickable"
                          onClick={() => {
                            if (onChange) {
                              onChange(option.id);
                            }
                            setIsDropdownOpen(false);
                            setDropdownInfo(undefined);
                          }}
                        >
                          <span className="option-text">{option.title}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        <div className="flow-field">
          <div className="field-input field-dropdown">
            <input
              onClick={() => {
                setDropdownInfo({
                  title: element.label,
                  options: element["data-source"],
                });
                setIsDropdownOpen(true);
              }}
              value={
                element["data-source"]?.find((option) => option.id === value)
                  ?.title || ""
              }
              className="input"
              placeholder={`${element.label}${
                element.required ? "" : " (optional)"
              }`}
              readOnly
            />
            <i className="fas fa-chevron-right"></i>
          </div>
          {hasError && <div className="field-error">{fieldError[0]}</div>}
        </div>
      </>
    );
  }
  if (element.type === "OptIn") {
    return (
      <div className="flow-field">
        <div className="field-optin">
          <label className="checkbox">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => {
                if (onChange) {
                  onChange(e.target.checked ? true : undefined);
                }
              }}
            />
            <span className="optin-label">{element.label}</span>
          </label>
        </div>
        {hasError && <div className="field-error">{fieldError[0]}</div>}
      </div>
    );
  }

  return null;
};

export default WhatsAppPreview;
